import { type Question } from "@jti/ui/components/SobQuiz/SobQuiz.types";

import { buildUuid } from "./buildUuid";

export type InputAnswer = {
  title: string;
  referenceName: string;
  nextQuestionId?: string;
};

export type InputQuestion = {
  id: string;
  referenceName: string;
  title: string;
  answers: InputAnswer[];
};

type QuizQuestionPayload = {
  question_id: string;
  reference_name: string;
  local_translation: string;
  type: string;
};

type TransitionAnswer = {
  id: string;
  title: string;
  referenceName: string;
  localTranslation: string;
};

type QuizAnswerPayload = QuizQuestionPayload & {
  answer_id: string;
};

type PartialSobQuizProps = {
  quizTransitions: Question[];
  answerDimensions: { entity: string; payload: QuizAnswerPayload[] };
  questionDimensions: { entity: string; payload: QuizQuestionPayload[] };
  uuid: string;
};

export function transformSobData(questions?: InputQuestion[], uuid?: string): PartialSobQuizProps {
  if (!questions || questions.length === 0) {
    return {
      quizTransitions: [],
      answerDimensions: { entity: "quiz_answer_raw", payload: [] },
      questionDimensions: { entity: "quiz_question_raw", payload: [] },
      uuid: uuid || buildUuid([], []),
    };
  }

  const questionMap = new Map<string, InputQuestion>();
  questions.forEach((q) => questionMap.set(q.id, q));

  const questionDimensions: QuizQuestionPayload[] = [];
  const answerDimensions: QuizAnswerPayload[] = [];
  const answerIdMap: Record<string, string[]> = {};

  questions.forEach((q) => {
    questionDimensions.push({
      question_id: q.id,
      reference_name: q.referenceName.trim(),
      local_translation: q.title,
      type: "SOURCE_OF_BUSINESS",
    });

    answerIdMap[q.id] = [];
    q.answers.forEach((ans, idx) => {
      const answerId = `${q.id}-${idx}`;
      const answerIdsForQuestion = answerIdMap[q.id];

      if (answerIdsForQuestion) {
        answerIdsForQuestion.push(answerId);
      }

      answerDimensions.push({
        question_id: q.id,
        answer_id: answerId,
        reference_name: ans.referenceName,
        local_translation: ans.title,
        type: "SOURCE_OF_BUSINESS",
      });
    });
  });

  function getPossibleAnswers(q: InputQuestion): TransitionAnswer[] {
    return q.answers.flatMap((ans, idx) => {
      const id = answerIdMap?.[q.id]?.[idx];

      if (!id) {
        return [];
      }

      return {
        id,
        title: ans.title,
        referenceName: ans.referenceName,
        localTranslation: ans.title,
      };
    });
  }

  const quizTransitions: Question[] = [];

  if (questions.length > 0) {
    const first = questions[0];

    if (first) {
      quizTransitions.push({
        question: { id: first.id, title: first.title },
        possibleAnswers: getPossibleAnswers(first),
      });
    }
  }

  questions.forEach((q) => {
    q.answers.forEach((ans, idx) => {
      if (ans.nextQuestionId) {
        const nextQ = questionMap.get(ans.nextQuestionId);
        const onAnswerId = answerIdMap?.[q.id]?.[idx];
        if (nextQ) {
          quizTransitions.push({
            onAnswerId,
            question: { id: nextQ.id, title: nextQ.title },
            possibleAnswers: getPossibleAnswers(nextQ),
          });
        }
      }
    });
  });

  return {
    quizTransitions,
    answerDimensions: {
      entity: "quiz_answer_raw",
      payload: answerDimensions,
    },
    questionDimensions: {
      entity: "quiz_question_raw",
      payload: questionDimensions,
    },
    uuid: uuid || buildUuid(answerDimensions, questionDimensions),
  };
}
